import { isAndroid } from "@/libs/software"

export const getInferenceDelegate = (): "CPU" | "GPU" => (guessSamsungGalaxyS25() ? "CPU" : "GPU")

/**
 * There is a bug on samsung galaxy S25, S25+ and S25 Ultra that breaks any detections when GPU delegate is used
 * This is a guesser function that narrows down the device's gpu/navigator/window properties
 * and returns true if they resemble samsung galaxy S25, S25+ or S25 Ultra (which were received using lambdatest's devices)
 *
 * Issues links:
 * - https://github.com/google-ai-edge/mediapipe/issues/5908
 * - https://github.com/google-ai-edge/mediapipe/issues/5867
 */
const guessSamsungGalaxyS25 = () => {
  if (!isAndroid) return false
  if (navigator.hardwareConcurrency !== 8) return false // S25 models have 8 cores
  if (navigator.maxTouchPoints !== 5) return false // S25 models support 5 touch points

  // Known screen dimensions for S25 models in portrait mode with CSS pixels
  const sizes: [w: number, h: number][] = [
    [360, 780], // S25
    [384, 832], // S25+
    [412, 892], // S25 Ultra
    [384, 824],
  ]
  const { width, height } = screen

  if (!sizes.some(([w, h]) => width === w && height === h)) return false

  try {
    const canvas = document.createElement("canvas")
    const gl = canvas.getContext("webgl")! // Using WebGL to detect GPU information which is more reliable for device identification
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info")! // WEBGL_debug_renderer_info is an extension that allows access to GPU vendor and renderer
    const vendor = (gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string).toLowerCase().trim()
    const renderer = (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
      .toLowerCase()
      .trim()

    // S25 models use Qualcomm chips with Adreno 830+ GPUs
    // The regex extracts the numeric part from strings like "Adreno (TM) 830"
    return (
      vendor.includes("qualcomm") &&
      !vendor.includes("google") && // Exclude Google-branded devices with Qualcomm chips
      parseInt(renderer.match(/adreno.*?(\d+)/i)![1]) >= 830
    )
  } catch {
    return false
  }
}
